import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Mode } from '../types/mode'

type DrawerContextState = {
  mode: Mode
  isOpen: boolean
  isExpanded: boolean
  open: () => void
  openCompact: () => void
  openExpanded: () => void
  close: () => void
  requestClose: () => void
  toggleExpanded: () => void
}

type DrawerContextProviderProps = {
  children: ReactNode
  drawerMode?: Mode
  onCloseRequest?: () => void
  onDrawerModeChange?: (mode: Mode) => void
}

const DrawerContext = createContext<DrawerContextState | undefined>(undefined)

const isOpenMode = (mode: Mode): boolean => mode !== Mode.Closed

export const DrawerContextProvider = ({
  children,
  drawerMode = Mode.Closed,
  onCloseRequest,
  onDrawerModeChange,
}: DrawerContextProviderProps): ReactElement => {
  const [mode, setMode] = useState<Mode>(drawerMode)

  const setModeAndNotify = useCallback(
    (next: Mode) => {
      setMode(next)
      onDrawerModeChange?.(next)
    },
    [onDrawerModeChange],
  )

  const open = useCallback(() => {
    setModeAndNotify(Mode.Opened)
  }, [setModeAndNotify])

  const openCompact = useCallback(() => {
    setModeAndNotify(Mode.Compact)
  }, [setModeAndNotify])

  const openExpanded = useCallback(() => {
    setModeAndNotify(Mode.Expanded)
  }, [setModeAndNotify])

  const close = useCallback(() => {
    setModeAndNotify(Mode.Closed)
  }, [setModeAndNotify])

  const requestClose = useCallback(() => {
    if (onCloseRequest) {
      onCloseRequest()
    } else {
      setModeAndNotify(Mode.Closed)
    }
  }, [onCloseRequest, setModeAndNotify])

  const toggleExpanded = useCallback(() => {
    setModeAndNotify(mode === Mode.Expanded ? Mode.Compact : Mode.Expanded)
  }, [mode, setModeAndNotify])

  useEffect(() => {
    setMode(drawerMode)
  }, [drawerMode])

  const value = useMemo<DrawerContextState>(
    () => ({
      mode,
      isOpen: isOpenMode(mode),
      isExpanded: mode === Mode.Expanded,
      open,
      openCompact,
      openExpanded,
      close,
      requestClose,
      toggleExpanded,
    }),
    [mode, open, openCompact, openExpanded, close, requestClose, toggleExpanded],
  )

  return <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>
}

export const useDrawer = (): DrawerContextState => {
  const context = useContext(DrawerContext)

  if (context === undefined) {
    throw new Error('DrawerContextProvider is missing in the module!')
  }

  return context
}
